import {
  ApiClient,
  type ApiClientOptions,
  type CursorPagedResponse,
} from '@/infra/api-client'
import { toURLSearchParams } from '@/utils/url-util'
import type {
  BillingMethodResource,
  CreateBillingMethodRequest,
  CreateSetupIntentRequest,
  SetupIntentResource,
} from '@/features/billing/api/resources/billing-method'
import type { BillingOperationResponse } from '@/features/billing/api/resources/billing-task-result'
import type {
  CheckoutLinkResource,
  EmailCheckoutLinkRequest,
} from '@/features/billing/api/resources/checkout-link'
import type {
  BillingProfileResource,
  UpdateBillingProfileRequest,
} from '@/features/billing/api/resources/billing-profile'
import type {
  CouponResource,
  ListCouponsRequest,
  UserCouponResource,
} from '@/features/billing/api/resources/coupon'
import type {
  BillingTransactionResource,
  GetManyBillingTransactionsRequest,
} from '@/features/billing/api/resources/billing-transaction'

/**
 * Collection of APIs for billing.
 */
export class BillingApi {
  readonly profiles
  readonly methods
  readonly transactions
  readonly coupons
  readonly userCoupons
  readonly checkout

  constructor(options: ApiClientOptions) {
    this.profiles = new BillingProfileApi(options)
    this.methods = new BillingMethodApi(options)
    this.transactions = new BillingTransactionApi(options)
    this.coupons = new CouponApi(options)
    this.userCoupons = new UserCouponApi(options)
    this.checkout = new CheckoutApi(options)
  }
}

/**
 * API for billing profiles.
 */
export class BillingProfileApi extends ApiClient {
  getForMember(
    workspaceId: string,
    userPublicId: string,
  ): Promise<BillingProfileResource> {
    return this._get(
      `v3/workspaces/${workspaceId}/billing-profiles/${userPublicId}`,
    )
  }

  getForTeam(teamId: string): Promise<BillingProfileResource> {
    return this._get(`v3/teams/${teamId}/billing-profile`)
  }

  updateForMember(
    workspaceId: string,
    userPublicId: string,
    request: UpdateBillingProfileRequest,
  ): Promise<BillingProfileResource> {
    return this._patch(
      `v3/workspaces/${workspaceId}/billing-profiles/${userPublicId}`,
      request,
    )
  }

  updateForTeam(
    teamId: string,
    request: UpdateBillingProfileRequest,
  ): Promise<BillingProfileResource> {
    return this._patch(`v3/teams/${teamId}/billing-profile`, request)
  }
}

/**
 * API for billing methods.
 */
export class BillingMethodApi extends ApiClient {
  create(request: CreateBillingMethodRequest): Promise<BillingMethodResource> {
    return this._post('v3/billing-methods', request)
  }

  delete(id: string): Promise<BillingOperationResponse> {
    return this._delete(`v3/billing-methods/${id}`)
  }

  createStripeSetupIntent(
    request: CreateSetupIntentRequest,
  ): Promise<SetupIntentResource> {
    return this._post(`v3/stripe-payment-methods/setup-intent`, request)
  }

  completeStripeSetupIntent(
    setupIntentId: string,
  ): Promise<BillingMethodResource> {
    return this._post(`v3/stripe-payment-methods/setup-intent/complete`, {
      setup_intent_id: setupIntentId,
    })
  }
}

/**
 * API for billing transactions.
 */
export class BillingTransactionApi extends ApiClient {
  getMany(
    request: GetManyBillingTransactionsRequest,
  ): Promise<BillingTransactionResource[]> {
    return this._post(`v3/transactions/id-query`, request)
  }
}

/**
 * API for coupons.
 */
export class CouponApi extends ApiClient {
  list(
    workspaceId: string,
    request: ListCouponsRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<CouponResource>> {
    return this._get(
      `v3/workspaces/${workspaceId}/coupons`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }

  get(id: string, signal?: AbortSignal): Promise<CouponResource> {
    return this._get(`v3/coupons/${id}`, undefined, { signal })
  }

  getByCode(
    workspaceId: string,
    code: string,
    signal?: AbortSignal,
  ): Promise<CouponResource> {
    return this._get(
      `v3/workspaces/${workspaceId}/coupons/${code}`,
      undefined,
      { signal },
    )
  }
}

/**
 * API for the coupons added to users.
 */
export class UserCouponApi extends ApiClient {
  list(
    workspaceId: string,
    userPublicId: string,
    signal?: AbortSignal,
  ): Promise<UserCouponResource[]> {
    return this._get(
      `v3/workspaces/${workspaceId}/users/${userPublicId}/coupons`,
      undefined,
      { signal },
    )
  }

  add(
    workspaceId: string,
    userPublicId: string,
    code: string,
  ): Promise<UserCouponResource> {
    return this._post(
      `v3/workspaces/${workspaceId}/users/${userPublicId}/coupons/${code}`,
      undefined,
    )
  }
}

/**
 * API for Stripe checkout links.
 */
export class CheckoutApi extends ApiClient {
  generateCheckoutLink(jobId: string): Promise<CheckoutLinkResource> {
    return this._post(`v3/stripe/generate-checkout-link/${jobId}`, undefined)
  }

  emailCheckoutLink(request: EmailCheckoutLinkRequest): Promise<void> {
    return this._post(`v3/stripe/email-checkout-link`, request)
  }

  invalidateCheckoutSessions(jobId: string, force: boolean): Promise<void> {
    return this._post(`v3/stripe/invalidate-checkout-sessions/${jobId}`, {
      force,
    })
  }
}
